// 테스트 따라하기(투어) — 시나리오 절차 문장에서 화면 요소를 글자로 찾아 ①②③ 배지와 말풍선을 붙인다 (2026-10-18).
// 라이브러리 없이: 선택자는 깨지기 쉬우니 "버튼 글자 · 탭 이름 · 폼 라벨" 로 찾고,
// 화면 담당이 [위치 지정]으로 집어 준 anchors(시나리오 필드)가 있으면 그걸 우선 쓴다.

const IGNORE_SEL = '.qa-tour-ignore, .qa-drawer-nomodal, .el-drawer, .help-tabs, .qa-tour-layer'

export function norm(s) {
  return (s || '').replace(/[\s+*:：\uFF0B\u25BE\u25B8\u2192\u203A\u00BB\u00B7\u2022\u200B]/g, '').replace(/\(.*?\)/g, '').toLowerCase()
}

/** 절차/입력값 → 투어 단계 목록 */
export function buildSteps(scenario) {
  const steps = []
  const proc = scenario.procedure || []
  const inputs = (scenario.inputs || []).map(x => ({ ...x }))
  const used = new Set()
  const pushInput = (inp) => {
    if (inp.label) steps.push({ kind: 'input', label: inp.label, value: inp.value, text: `"${inp.label}" 에 입력: ${inp.value}` })
    else steps.push({ kind: 'text', value: inp.value, text: `입력: ${inp.value}` })
  }
  proc.forEach((line, li) => {
    const text = String(line || '').trim()
    if (/^이동\s*[:：]/.test(text)) {
      const body = text.replace(/^이동\s*[:：]\s*/, '')
      // 메뉴 경로(A > B)는 이미 그 화면 — [버튼]·'탭'·'행 클릭' 만 앵커
      const parts = body.split(/\s*(?:>|→|›)\s*/)
      let anchored = false
      parts.forEach((p) => {
        const btn = [...p.matchAll(/\[([^\]]+)\]/g)].map(m => m[1].trim())
        const noBtn = p.replace(/\[[^\]]+\]/g, ' ')
        const tabs = [...noBtn.matchAll(/([\w가-힣/.()\s]+?)\s*탭/g)].map(m => m[1].trim()).filter(Boolean)
        tabs.forEach((t) => { steps.push({ kind: 'tab', label: t, text: `"${t}" 탭을 누르세요`, li }); anchored = true })
        if (/(해당|첫|테스트)\s*(건|행)|행\s*(을|를)?\s*(클릭|선택)|건\s*(을|를)?\s*(클릭|열기|선택)/.test(noBtn)) {
          const q = noBtn.match(/[(（]([^)）]+)[)）]/)
          steps.push({ kind: 'row', value: q ? q[1].trim() : '', text: q ? `목록에서 "${q[1].trim()}" 행을 클릭하세요` : '목록에서 테스트 건(첫 행)을 클릭하세요', li }); anchored = true
        }
        btn.forEach((b) => {
          const row = /행\s*$/.test(noBtn.trim()) || /해당 행|행 \[/.test(p)
          steps.push({ kind: 'button', label: b, scopeRow: row, text: `[${b}] 를 누르세요`, li }); anchored = true
        })
      })
      // 메뉴 경로뿐인 첫 줄("자재 > 자재 마스터")은 이미 그 화면에 와 있으니 단계에서 뺀다
      if (!anchored && !(li === 0 && !/휴대폰|모바일|계정/.test(body))) steps.push({ kind: 'text', text: `화면: ${body}`, li })
      steps.forEach(st => { if (st.li === undefined) st.li = li })
      return
    }
    if (/^입력\s*[:：]/.test(text)) {
      const body = text.replace(/^입력\s*[:：]\s*/, '')
      // 이 줄에 라벨이 적힌 입력값만 — 없으면 아직 안 쓴 입력값 전부 (한 줄 '입력:' 시나리오)
      let mine = inputs.filter((inp, i) => !used.has(i) && inp.label && body.includes(inp.label)).map(inp => inputs.indexOf(inp))
      if (!mine.length) mine = inputs.map((_, i) => i).filter(i => !used.has(i))
      if (mine.length) mine.forEach((i) => { used.add(i); pushInput(inputs[i]); steps[steps.length - 1].li = li })
      else steps.push({ kind: 'text', text, li })
      return
    }
    if (/^(저장|실행|저장\/실행)/.test(text)) {
      // 마무리 단계 — 열린 팝업의 저장 버튼을 가리키되, 없어도 "못 찾음"이 아니다(조회만 하는 시나리오)
      const m = text.match(/\[([^\]]+)\]/)
      steps.push({ kind: 'finish', label: m ? m[1].trim() : '', soft: true, li,
        text: m ? `[${m[1].trim()}] 를 누르고, 서랍의 "확인할 것"을 체크하세요` : '저장(실행)한 뒤, 서랍의 "확인할 것"을 체크하세요' })
      return
    }
    const btns = [...text.matchAll(/\[([^\]]+)\]/g)].map(m => m[1].trim())
    if (btns.length) { btns.forEach(b => steps.push({ kind: 'button', label: b, text: `[${b}] 를 누르세요`, li })); return }
    steps.push({ kind: 'text', text, li })
  })
  // 담당자가 집어 준 위치(anchors: {stepIndex: {selector, text}})
  const anchors = scenario.anchors || {}
  steps.forEach((s, i) => { if (anchors[i]) s.anchor = anchors[i] })
  return steps
}

/** 화면에서 가리킬 수 있는 단계인가 (text 는 아니고, finish 는 찾히면 가리킨다) */
export function isAnchorStep(s) { return s.kind !== 'text' && !s.soft }

function visible(el) {
  if (!el || !el.isConnected) return false
  if (el.closest(IGNORE_SEL)) return false
  const r = el.getBoundingClientRect()
  if (!r.width || !r.height) return false
  const st = getComputedStyle(el)
  return st.visibility !== 'hidden' && st.display !== 'none'
}

/** 실제로 맨 위에 보이는가 — 팝업 뒤에 가려진 요소(목록 화면의 같은 이름 필터 등)를 걸러낸다 */
function onTop(el) {
  const r = el.getBoundingClientRect()
  const x = Math.min(window.innerWidth - 2, Math.max(1, r.left + Math.min(r.width / 2, 12)))
  const y = Math.min(window.innerHeight - 2, Math.max(1, r.top + Math.min(r.height / 2, 12)))
  const hit = document.elementFromPoint(x, y)
  if (!hit) return false
  if (hit.closest('.qa-tour-layer')) return true  // 우리 배지가 덮고 있으면 통과
  return el === hit || el.contains(hit) || hit.contains(el)
}

/** 후보 정렬 — 열린 다이얼로그 안 → 맨 위에 보이는 것 → 나머지 */
function prioritize(list) {
  const vis = list.filter(visible)
  const dialogs = [...document.querySelectorAll('.el-overlay-dialog, .el-dialog')].filter(visible)
  const top = dialogs.length ? dialogs[dialogs.length - 1] : null
  const score = el => (top && top.contains(el) ? 0 : 2) + (onTop(el) ? 0 : 1)
  return vis.map(el => [score(el), el]).sort((a, b) => a[0] - b[0]).map(x => x[1])
}

function pickVisible(list) {
  const vis = list.filter(visible)
  if (!vis.length) return null
  // 열려 있는 다이얼로그가 있으면 그 안을 우선 — 사용자가 보는 건 팝업
  const dialogs = [...document.querySelectorAll('.el-overlay-dialog, .el-dialog')].filter(visible)
  if (dialogs.length) {
    const top = dialogs[dialogs.length - 1]
    const inside = vis.filter(el => top.contains(el))
    if (inside.length) return inside.find(onTop) || inside[0]
  }
  // 열린 탭 안의 것을 바깥(페이지 머리 버튼)보다 우선 — 메인오더 [수정] vs 생산지시서 탭 안 [수정]
  const inPane = vis.filter(el => el.closest('.el-tab-pane'))
  if (inPane.length && inPane.length < vis.length) return inPane.find(onTop) || inPane[0]
  return vis.find(onTop) || vis[0]
}

function byText(selector, label, exact = true) {
  const want = norm(label)
  if (!want) return []
  const els = [...document.querySelectorAll(selector)]
  const scored = []
  for (const el of els) {
    const t = norm(el.innerText || el.textContent || el.getAttribute('aria-label') || el.getAttribute('title'))
    if (!t) continue
    if (t === want) scored.push([0, el])
    else if (!exact && t.includes(want) && !(/취소|해제|삭제/.test(t) && !/취소|해제|삭제/.test(want))) scored.push([1, el])  // '확정' 이 '확정 취소' 에 붙지 않게
    else if (!exact && want.includes(t) && t.length >= 2) scored.push([2, el])
  }
  scored.sort((a, b) => a[0] - b[0])
  return scored.map(x => x[1])
}

const BTN = 'button, .el-button, a.el-link, .el-dropdown-link, [role=button], .el-dropdown-menu__item, .el-radio-button__inner, .el-radio__label, .el-checkbox-button__inner, .el-check-tag, .el-segmented__item-label'
const LAB = '.el-form-item__label, label, th, .el-table__cell.is-leaf .cell, .ag-header-cell-text, .form-label, .field-label, .el-descriptions__label, dt'
const INP = 'input:not([type=hidden]), textarea, .el-select, .el-input, .el-date-editor, .el-input-number, .el-radio-group, .el-checkbox-group, .el-switch, .el-upload, .el-cascader'

function topDialog() {
  const dialogs = [...document.querySelectorAll('.el-overlay-dialog, .el-dialog, .el-message-box')].filter(visible)
  return dialogs.length ? dialogs[dialogs.length - 1] : null
}

function findButton(labels, exactOnly) {
  for (const lab of labels) {
    let els = byText(BTN, lab, true)
    if (!els.length && !exactOnly) els = byText(BTN, lab, false)
    const el = pickVisible(els)
    if (el) return el
  }
  return null
}

function inputNear(lab) {
  const item = lab.closest('.el-form-item') || lab.closest('.el-descriptions__cell')?.nextElementSibling || lab.parentElement
  const inp = item && item.querySelector(INP)
  if (inp && visible(inp)) return inp
  // 표 머리(th / ag-grid 헤더) → 같은 열의 첫 입력칸, 없으면 첫 셀
  const th = lab.closest('th, .ag-header-cell')
  if (th) {
    if (th.classList.contains('ag-header-cell')) {
      const id = th.getAttribute('col-id')
      const cell = id && document.querySelector(`.ag-center-cols-container .ag-row .ag-cell[col-id="${CSS.escape(id)}"]`)
      if (cell && visible(cell)) return cell
    } else {
      const idx = [...th.parentElement.children].indexOf(th)
      const row = th.closest('table')?.querySelector('tbody tr') || th.closest('.el-table')?.querySelector('.el-table__body tbody tr')
      const cell = row && row.children[idx]
      if (cell && visible(cell)) return cell.querySelector(INP) || cell
    }
  }
  return null
}

function findInput(label, value) {
  // 1) 폼 라벨
  let labs = byText(LAB, label, true)
  if (!labs.length) labs = byText(LAB, label, false)
  for (const lab of prioritize(labs)) { const el = inputNear(lab); if (el) return el }
  // 2) 메시지 박스(확인창)의 입력칸
  const mb = document.querySelector('.el-message-box .el-message-box__input input')
  if (mb && visible(mb)) return mb
  // 3) placeholder 에 라벨 글자가 들어 있는 입력칸
  const want = norm(label)
  const ph = [...document.querySelectorAll('input[placeholder], textarea[placeholder]')].filter(el => norm(el.getAttribute('placeholder')).includes(want))
  const el3 = pickVisible(ph)
  if (el3) return el3
  // 4) 값이 버튼/라디오/탭 글자인 경우 ('프리셋' = [환율 +5%], '자재구분' = 원자재)
  if (value) {
    const v = String(value).replace(/^['"‘“]|['"’”]$/g, '')
    const b = findButton([v], true) || pickVisible(byText('.el-tabs__item, [role=tab]', v, true))
    if (b) return b
  }
  // 5) 표의 행 이름 (원단 행, BLACK 행)
  const cells = byText('td, .el-table__cell, .ag-cell', label, false)
  const c = pickVisible(cells)
  if (c) return c.querySelector(INP) || c
  return null
}

/** 단계 → 화면 요소 (없으면 null) */
export function resolveStep(step) {
  try {
    if (step.anchor && step.anchor.selector) {
      const el = pickVisible([...document.querySelectorAll(step.anchor.selector)])
      if (el) return el
    }
    if (step.anchor && !step.anchor.selector && step.anchor.text) {
      const el = resolveStep({ kind: step.anchor.kind || 'button', label: step.anchor.text })
      if (el) return el
    }
    if (step.kind === 'button') {
      const labels = [...(step.label ? [step.label] : []), ...(step.anyOf || [])]
      return findButton(labels, !!step.exactOnly)
    }
    if (step.kind === 'finish') {
      // 라벨이 적혀 있으면 그 버튼, 아니면 열린 팝업의 기본(primary) 버튼 → 팝업 안 저장류 → 화면의 [저장]
      if (step.label) { const b = findButton([step.label], false); if (b) return b }
      const dlg = topDialog()
      if (dlg) {
        const prim = [...dlg.querySelectorAll('.el-dialog__footer .el-button--primary, .el-message-box__btns .el-button--primary, .dialog-footer .el-button--primary')].filter(visible)
        if (prim.length) return prim[prim.length - 1]
        const b = findButton(['저장', '등록', '생성', '추가', '확인', '적용', '실행'], true)
        if (b && dlg.contains(b)) return b
      }
      const b = pickVisible(byText(BTN, '저장', true))
      return b || null
    }
    if (step.kind === 'tab') {
      let els = byText('.el-tabs__item, [role=tab], .el-radio-button__inner, .el-segmented__item-label', step.label, true)
      if (!els.length) els = byText('.el-tabs__item, [role=tab], .el-radio-button__inner, .el-segmented__item-label', step.label, false)
      return pickVisible(els)
    }
    if (step.kind === 'input') return findInput(step.label, step.value)
    if (step.kind === 'row') {
      if (step.value) {
        const cells = byText('td, .el-table__cell, .ag-cell', step.value, false)
        const el = pickVisible(cells)
        if (el) return el.closest('tr, .ag-row') || el
      }
      const first = [...document.querySelectorAll('.el-table__body tbody tr, .ag-center-cols-container .ag-row, table tbody tr')]
      const el = pickVisible(first)
      return el || null
    }
  } catch (e) { /* 선택자 오류 등 */ }
  return null
}

/** 사용자가 집은 요소 → 저장용 앵커 */
export function describeElement(el) {
  if (!el) return null
  const qa = el.closest('[data-qa]')
  if (qa) return { selector: `[data-qa="${qa.getAttribute('data-qa')}"]`, text: (el.innerText || '').trim().slice(0, 40) }
  if (el.id && !/^\d|el-id-/.test(el.id)) return { selector: `#${CSS.escape(el.id)}`, text: (el.innerText || '').trim().slice(0, 40) }
  const btn = el.closest('button, .el-button, .el-tabs__item, a')
  if (btn) {
    const t = (btn.innerText || '').trim().slice(0, 40)
    return { selector: '', text: t, kind: btn.classList.contains('el-tabs__item') ? 'tab' : 'button' }
  }
  const item = el.closest('.el-form-item')
  if (item) {
    const lab = item.querySelector('.el-form-item__label')
    return { selector: '', text: (lab?.innerText || '').trim(), kind: 'input' }
  }
  // 마지막 수단: 태그+클래스 경로 (3단)
  const path = []
  let cur = el
  for (let i = 0; i < 3 && cur && cur !== document.body; i++) {
    const cls = [...cur.classList].filter(c => !/^is-|^el-.*--/.test(c)).slice(0, 2).map(c => '.' + CSS.escape(c)).join('')
    path.unshift(cur.tagName.toLowerCase() + cls)
    cur = cur.parentElement
  }
  return { selector: path.join(' > '), text: (el.innerText || '').trim().slice(0, 40) }
}
